import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check, ChevronDown, ChevronUp, Plus, Send, Trash2, X } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  BRIEF_SECTIONS, WEB_ANOMALY_CATEGORIES, WEB_ANOMALY_STATUS, WEB_DESIGN_KINDS,
  WEB_ELEMENT_KINDS, WEB_ELEMENT_STATUS, WEB_GOLIVE_CHECKLIST, WEB_OP_STATUSES,
  WEB_PAGE_STATUS, WEB_SEO_GLOBAL, WEB_STAGES, WEB_TASK_CATEGORIES,
  WEB_VERSION_STATUS, webCmsLabel, webTypeLabel,
} from "@/lib/constants";
import { ApercuTab, PlanningTab } from "@/components/WebApercu";
import { opBadge } from "@/components/WebProjectPanel";

const sel = "border border-neutral-200 rounded-md px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-neutral-300";
const fmtD = (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : "—");
const fmtH = (sec) => `${Math.floor((sec || 0) / 3600)}h${String(Math.round(((sec || 0) % 3600) / 60)).padStart(2, "0")}`;
const eur = (v) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v || 0);
const initialsOf = (name = "") => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "·";

const Badge = ({ st }) => (
  <span className={`text-[11px] border rounded-full px-2 py-0.5 ${st.cls}`}>{st.label}</span>
);

const TABS = [
  ["apercu", "Aperçu"], ["pages", "Pages"], ["taches", "Tâches"], ["fichiers", "Fichiers"],
  ["echanges", "Échanges"], ["planning", "Planning"], ["parametres", "Paramètres"],
];

export default function WebProject() {
  const { pid } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [client, setClient] = useState(null);
  const [tab, setTab] = useState("apercu");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/web/projects/${pid}`).then((r) => setP(r.data)).catch(() => setP(false));
  useEffect(() => { load(); }, [pid]);
  useEffect(() => {
    if (!p?.client_id) return;
    api.get("/clients").then((r) => setClient((r.data || []).find((c) => c.id === p.client_id) || null)).catch(() => {});
  }, [p?.client_id]); // eslint-disable-line

  const act = async (fn, msg) => {
    setBusy(true);
    try { const r = await fn(); if (r?.data) setP(r.data); if (msg) toast.success(msg); else load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  const patch = (body, msg) => act(() => api.patch(`/web/projects/${pid}`, body), msg);

  if (p === null) return <div className="py-20 flex justify-center"><div className="w-6 h-6 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" /></div>;
  if (p === false) return <p className="py-20 text-center text-neutral-400">Projet introuvable.</p>;

  const badge = opBadge(p.op_status, p.stage);
  const clientSince = client?.created_at ? new Date(client.created_at).getFullYear() : null;

  return (
    <div className="flex flex-col gap-3 h-[calc(100vh-7.5rem)] overflow-hidden" data-testid="web-project-page">
      {/* En-tête */}
      <div className="shrink-0">
        <button onClick={() => navigate("/sites-web")} className="text-xs text-neutral-400 hover:text-neutral-900 flex items-center gap-1 transition-colors" data-testid="web-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Retour aux projets
        </button>
        <button onClick={async () => { try { await api.post("/tasks", { title: `Projet web — ${p.name}`, client_id: p.client_id, service: "Web Design", source_type: "web_project", source_id: p.id, source_label: `Projet web — ${p.name}`, source_url: `/sites-web/projet/${p.id}` }); toast.success("Tâche ajoutée à la To-do"); window.dispatchEvent(new Event("sf:tasks")); } catch { toast.error("Création impossible"); } }} data-testid="web-add-task"
          className="mt-1 text-[11px] font-semibold text-[#1D4ED8] hover:underline flex items-center gap-1">
          <Plus className="w-3 h-3" /> Ajouter une tâche à la To-do
        </button>
        <div className="flex flex-wrap items-start justify-between gap-3 mt-1.5">
          <div className="min-w-0">
            <h1 className="font-heading text-2xl font-bold flex items-center gap-3" data-testid="web-project-name">
              {p.name}
              <span className={`text-[10px] font-semibold border rounded-full px-2.5 py-1 ${badge.cls}`} data-testid="web-project-badge">{badge.label}</span>
            </h1>
            <p className="text-sm text-neutral-500 mt-0.5">{webTypeLabel(p.type)} · {webCmsLabel(p.cms)}</p>
            {p.description && <p className="text-xs text-neutral-400 mt-1 max-w-2xl leading-relaxed line-clamp-2">{p.description}</p>}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <select className="text-xs border border-neutral-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none" value={p.stage}
              onChange={(e) => patch({ stage: e.target.value })} data-testid="web-stage-select">
              {WEB_STAGES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
            <select className="text-xs border border-neutral-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none" value={p.op_status}
              onChange={(e) => patch({ op_status: e.target.value })} data-testid="web-opstatus-select">
              {WEB_OP_STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
            <Link to={`/clients/${p.client_id}`} data-testid="web-client-card"
              className="flex items-center gap-2.5 bg-white border border-[#E2E8F0] rounded-xl shadow-sm pl-2 pr-3 py-1.5 hover:border-neutral-300 transition-colors">
              <span className="w-8 h-8 rounded-lg bg-[#F1F5F9] text-[#0F172A] text-[10px] font-bold flex items-center justify-center">{initialsOf(p.client_name)}</span>
              <span>
                <span className="block text-xs font-bold text-[#0F172A] leading-tight">{p.client_name}</span>
                <span className="block text-[10px] text-neutral-400">{clientSince ? `Client depuis ${clientSince}` : "Voir la fiche client"} ›</span>
              </span>
            </Link>
          </div>
        </div>
      </div>

      {/* Onglets */}
      <div className="flex items-center gap-5 border-b border-neutral-200 shrink-0 overflow-x-auto" data-testid="web-tabs">
        {TABS.map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`wp-tab-${id}`}
            className={`pb-2 text-xs font-semibold border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === id ? "border-[#0B132B] text-[#0B132B]" : "border-transparent text-neutral-400 hover:text-neutral-700"}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Contenu */}
      {tab === "apercu" && <ApercuTab p={p} act={act} patch={patch} setTab={setTab} client={client} />}
      {tab === "pages" && <Scroll><Pages p={p} act={act} /></Scroll>}
      {tab === "taches" && <Scroll><Tasks p={p} act={act} /></Scroll>}
      {tab === "fichiers" && <Scroll><Design p={p} act={act} /></Scroll>}
      {tab === "echanges" && <Scroll><Messages p={p} act={act} /></Scroll>}
      {tab === "planning" && <PlanningTab p={p} />}
      {tab === "parametres" && <Parametres p={p} act={act} patch={patch} busy={busy} />}
    </div>
  );
}

const Scroll = ({ children }) => (
  <div className="flex-1 min-h-0 overflow-y-auto pr-0.5">{children}</div>
);

const PARAM_SUBS = [
  ["infos", "Infos générales"], ["brief", "Cahier des charges"], ["elements", "Éléments à fournir"],
  ["seo", "SEO"], ["tests", "Tests & anomalies"], ["golive", "Mise en ligne"],
  ["budget", "Budget"], ["maintenance", "Maintenance"],
];

function Parametres({ p, act, patch, busy }) {
  const [sub, setSub] = useState("infos");
  return (
    <div className="flex-1 min-h-0 flex flex-col gap-3" data-testid="wp-parametres">
      <div className="flex items-center gap-1.5 flex-wrap shrink-0">
        {PARAM_SUBS.map(([id, label]) => (
          <button key={id} onClick={() => setSub(id)} data-testid={`wps-tab-${id}`}
            className={`text-[11px] font-semibold rounded-full px-3 py-1.5 border transition-colors ${sub === id ? "bg-[#0B132B] border-[#0B132B] text-white" : "border-neutral-200 text-neutral-500 hover:border-neutral-400 bg-white"}`}>
            {label}
          </button>
        ))}
      </div>
      <Scroll>
        {sub === "infos" && <Overview p={p} patch={patch} act={act} />}
        {sub === "brief" && <Brief p={p} act={act} />}
        {sub === "elements" && <Elements p={p} act={act} busy={busy} />}
        {sub === "seo" && <Seo p={p} act={act} />}
        {sub === "tests" && <Tests p={p} act={act} />}
        {sub === "golive" && <GoLive p={p} act={act} />}
        {sub === "budget" && <Budget p={p} patch={patch} />}
        {sub === "maintenance" && <Maintenance p={p} act={act} />}
      </Scroll>
    </div>
  );
}

function Overview({ p, patch, act }) {
  const missing = (p.elements || []).filter((e) => e.status === "a_fournir" || e.status === "correction_demandee");
  const toVerify = (p.elements || []).filter((e) => e.status === "recu");
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-3" data-testid="web-actions-required">
        <h3 className="font-heading font-bold">Actions requises</h3>
        {missing.length === 0 && toVerify.length === 0 && !p.brief_a_valider && (
          <p className="text-sm text-neutral-400">Aucune action en attente.</p>
        )}
        {missing.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-amber-700 mb-1">Attendues du client</p>
            {missing.map((e) => (
              <p key={e.id} className="text-sm text-neutral-700 py-0.5" data-testid={`action-client-${e.id}`}>
                · {e.name}{e.due_date ? ` — avant le ${fmtD(e.due_date)}` : ""}
              </p>
            ))}
          </div>
        )}
        {toVerify.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-blue-700 mb-1">Attendues du webmaster</p>
            {toVerify.map((e) => (
              <p key={e.id} className="text-sm text-neutral-700 py-0.5" data-testid={`action-web-${e.id}`}>· Vérifier « {e.name} »</p>
            ))}
          </div>
        )}
        {p.brief_a_valider && <p className="text-sm text-blue-700">· Cahier des charges complété par le client — à valider.</p>}
      </div>
      <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-2" data-testid="web-overview-info">
        <h3 className="font-heading font-bold">Informations</h3>
        <Info label="Démarrage" value={fmtD(p.start_date)} />
        <Info label="Éléments reçus" value={`${p.elements_valides + p.elements_a_verifier} / ${p.elements_total}`} />
        <Info label="Tâches restantes" value={p.tasks_restantes} />
        <Info label="Temps estimé" value={p.estimated_minutes ? `${Math.round(p.estimated_minutes / 60)} h` : "—"} />
        <Info label="Temps réalisé" value={fmtH((p.tasks || []).reduce((a, t) => a + (t.spent_sec || 0), 0))} />
        {p.current_url && <Info label="Site actuel" value={<a href={p.current_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">{p.current_url}</a>} />}
        {p.domain && <Info label="Domaine prévu" value={p.domain} />}
        <div className="pt-2">
          <Label className="text-xs">URL de préproduction</Label>
          <div className="flex gap-2 mt-1">
            <Input defaultValue={p.preprod_url || ""} id="preprod-input" data-testid="web-preprod" />
            <Button size="sm" variant="outline" onClick={() => patch({ preprod_url: document.getElementById("preprod-input").value }, "URL enregistrée")} data-testid="web-preprod-save">OK</Button>
          </div>
        </div>
        {p.description && <p className="text-sm text-neutral-600 pt-2 border-t border-neutral-100">{p.description}</p>}
        {p.objective && <p className="text-sm text-neutral-500 italic">Objectif : {p.objective}</p>}
      </div>
    </div>
  );
}

const Info = ({ label, value }) => (
  <div className="flex justify-between text-sm"><span className="text-neutral-400">{label}</span><span className="font-medium text-right">{value}</span></div>
);

function Brief({ p, act }) {
  const brief = p.brief || {};
  const [answers, setAnswers] = useState(brief.answers || {});
  const [custom, setCustom] = useState(brief.custom_questions || []);
  const [newQ, setNewQ] = useState("");
  useEffect(() => { setAnswers(p.brief?.answers || {}); setCustom(p.brief?.custom_questions || []); }, [p.id]);
  const save = () => act(() => api.patch(`/web/projects/${p.id}/brief`, { answers, custom_questions: custom }), "Cahier des charges enregistré");
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-5" data-testid="web-brief">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-heading font-bold">Cahier des charges</h3>
        <div className="flex items-center gap-2">
          {brief.completed_at && <span className="text-[11px] text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5">Complété par le client</span>}
          {brief.validated_at
            ? <span className="text-[11px] text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5" data-testid="brief-validated">Validé le {fmtD(brief.validated_at)}</span>
            : <Button size="sm" variant="outline" onClick={() => act(() => api.post(`/web/projects/${p.id}/brief/validate`), "Cahier des charges validé")} data-testid="brief-validate">Valider le cahier des charges</Button>}
        </div>
      </div>
      {BRIEF_SECTIONS.map((sec) => (
        <div key={sec.id}>
          <h4 className="text-sm font-semibold mb-2">{sec.title}</h4>
          <div className="grid md:grid-cols-2 gap-3">
            {sec.questions.map(([key, label]) => (
              <div key={key}>
                <Label className="text-xs">{label}</Label>
                <Textarea rows={2} className="mt-1" value={answers[key] || ""}
                  onChange={(e) => setAnswers({ ...answers, [key]: e.target.value })} data-testid={`brief-${key}`} />
              </div>
            ))}
          </div>
        </div>
      ))}
      <div>
        <h4 className="text-sm font-semibold mb-2">Questions personnalisées</h4>
        {custom.map((q, i) => (
          <div key={q.id} className="flex items-start gap-2 mb-2">
            <div className="flex-1">
              <Label className="text-xs">{q.label}</Label>
              <Textarea rows={2} className="mt-1" value={answers[`custom_${q.id}`] || ""}
                onChange={(e) => setAnswers({ ...answers, [`custom_${q.id}`]: e.target.value })} data-testid={`brief-custom-${q.id}`} />
            </div>
            <button onClick={() => setCustom(custom.filter((_, j) => j !== i))} className="text-neutral-300 hover:text-red-500 mt-6" data-testid={`brief-custom-del-${q.id}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        <div className="flex gap-2">
          <Input value={newQ} onChange={(e) => setNewQ(e.target.value)} placeholder="Nouvelle question…" data-testid="brief-new-question" />
          <Button size="sm" variant="outline" onClick={() => { if (newQ.trim()) { setCustom([...custom, { id: `q${Date.now()}`, label: newQ.trim() }]); setNewQ(""); } }} data-testid="brief-add-question">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>
      <Button onClick={save} className="bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="brief-save">Enregistrer</Button>
    </div>
  );
}

function Elements({ p, act, busy }) {
  const [f, setF] = useState({ name: "", kind: "image", description: "", due_date: "", priority: "normale", responsible: "client" });
  const [tpl, setTpl] = useState("vitrine");
  const [refuseFor, setRefuseFor] = useState(null);
  const [refuseComment, setRefuseComment] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const add = () => {
    if (!f.name.trim()) { toast.error("Nom de l'élément requis"); return; }
    act(() => api.post(`/web/projects/${p.id}/elements`, { ...f, due_date: f.due_date || null }), "Demande créée");
    setF({ name: "", kind: "image", description: "", due_date: "", priority: "normale", responsible: "client" });
  };
  return (
    <div className="space-y-4" data-testid="web-elements">
      <div className="bg-white border border-neutral-200 rounded-xl p-4 flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-40"><Label className="text-xs">Nom de l'élément *</Label>
          <Input className="mt-1" value={f.name} onChange={set("name")} placeholder="Ex : Photos des réalisations" data-testid="element-name" /></div>
        <div><Label className="text-xs">Type</Label>
          <select className={`${sel} mt-1`} value={f.kind} onChange={set("kind")} data-testid="element-kind">
            {WEB_ELEMENT_KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
          </select></div>
        <div><Label className="text-xs">Échéance</Label>
          <Input type="date" className="mt-1" value={f.due_date} onChange={set("due_date")} data-testid="element-due" /></div>
        <div><Label className="text-xs">Responsable</Label>
          <select className={`${sel} mt-1`} value={f.responsible} onChange={set("responsible")} data-testid="element-responsible">
            <option value="client">Client</option><option value="webmaster">Webmaster</option>
          </select></div>
        <Button size="sm" onClick={add} disabled={busy} className="bg-neutral-900 text-white" data-testid="element-add"><Plus className="w-4 h-4" /></Button>
        <div className="w-full flex items-center gap-2 pt-1">
          <span className="text-xs text-neutral-400">Modèle de collecte :</span>
          <select className={sel} value={tpl} onChange={(e) => setTpl(e.target.value)} data-testid="element-template">
            <option value="vitrine">Site vitrine</option><option value="btp">Site BTP</option>
            <option value="immobilier">Site immobilier</option><option value="ecommerce">E-commerce</option>
            <option value="refonte">Refonte</option>
          </select>
          <Button size="sm" variant="outline" onClick={() => act(() => api.post(`/web/projects/${p.id}/elements/apply-template`, { template: tpl }), "Modèle appliqué")} data-testid="element-apply-template">Appliquer</Button>
          <span className="text-xs text-neutral-400 ml-auto">{p.elements_valides} validés / {p.elements_total}</span>
        </div>
      </div>

      {(p.elements || []).map((e) => {
        const st = WEB_ELEMENT_STATUS[e.status] || WEB_ELEMENT_STATUS.a_fournir;
        return (
          <div key={e.id} className="bg-white border border-neutral-200 rounded-xl p-4" data-testid={`element-${e.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold">{e.name}</p>
                {e.description && <p className="text-xs text-neutral-500 mt-0.5">{e.description}</p>}
                <p className="text-[11px] text-neutral-400 mt-1">
                  {WEB_ELEMENT_KINDS.find((k) => k.id === e.kind)?.label} · {e.responsible === "client" ? "Client" : "Webmaster"}
                  {e.due_date ? ` · Échéance ${fmtD(e.due_date)}` : ""} · {e.submissions?.length || 0} transmission(s)
                </p>
                {e.review_comment && <p className="text-xs text-orange-700 mt-1">« {e.review_comment} »</p>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge st={st} />
                {e.status === "recu" && (
                  <>
                    <button title="Accepter" onClick={() => act(() => api.post(`/web/projects/${p.id}/elements/${e.id}/review`, { action: "accepte" }), "Élément validé")}
                      className="text-green-600 hover:text-green-700" data-testid={`element-accept-${e.id}`}><Check className="w-4 h-4" /></button>
                    <button title="Demander une correction" onClick={() => { setRefuseFor(e.id); setRefuseComment(""); }}
                      className="text-orange-500 hover:text-orange-600" data-testid={`element-refuse-${e.id}`}><X className="w-4 h-4" /></button>
                  </>
                )}
                <button title="Supprimer" onClick={() => act(() => api.delete(`/web/projects/${p.id}/elements/${e.id}`))}
                  className="text-neutral-300 hover:text-red-500" data-testid={`element-delete-${e.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            {refuseFor === e.id && (
              <div className="flex gap-2 mt-3">
                <Input value={refuseComment} onChange={(ev) => setRefuseComment(ev.target.value)}
                  placeholder="Commentaire pour le client (obligatoire)…" data-testid={`element-refuse-comment-${e.id}`} />
                <Button size="sm" variant="outline" disabled={!refuseComment.trim()}
                  onClick={() => { act(() => api.post(`/web/projects/${p.id}/elements/${e.id}/review`, { action: "refuse", comment: refuseComment }), "Correction demandée"); setRefuseFor(null); }}
                  data-testid={`element-refuse-confirm-${e.id}`}>Envoyer</Button>
                <Button size="sm" variant="ghost" onClick={() => setRefuseFor(null)}>Annuler</Button>
              </div>
            )}
          </div>
        );
      })}
      {(p.elements || []).length === 0 && <p className="text-sm text-neutral-400 text-center py-6">Aucune demande pour l'instant — créez-en une ou appliquez un modèle.</p>}
    </div>
  );
}

function Pages({ p, act }) {
  const [name, setName] = useState("");
  const move = (pgid, dir) => {
    const ids = (p.pages || []).map((x) => x.id);
    const i = ids.indexOf(pgid); const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    act(() => api.post(`/web/projects/${p.id}/pages/reorder`, { order: ids }));
  };
  const setStatus = (pgid, field, value) => act(() => api.patch(`/web/projects/${p.id}/pages/${pgid}`, { [field]: value }));
  return (
    <div className="space-y-4" data-testid="web-pages">
      <div className="bg-white border border-neutral-200 rounded-xl p-4 flex gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nom de la page (ex : Réalisations)" data-testid="page-name" />
        <Button size="sm" className="bg-neutral-900 text-white" data-testid="page-add"
          onClick={() => { if (name.trim()) { act(() => api.post(`/web/projects/${p.id}/pages`, { name: name.trim() }), "Page ajoutée"); setName(""); } }}>
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      <div className="bg-white border border-neutral-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead><tr className="text-left text-xs text-neutral-400 border-b border-neutral-100">
            <th className="px-3 py-2" /><th className="px-3 py-2 font-medium">Page</th>
            <th className="px-3 py-2 font-medium">Contenu</th><th className="px-3 py-2 font-medium">Design</th>
            <th className="px-3 py-2 font-medium">Dév</th><th className="px-3 py-2 font-medium">SEO</th>
            <th className="px-3 py-2 font-medium">Validation</th><th className="px-3 py-2" />
          </tr></thead>
          <tbody>
            {(p.pages || []).map((pg) => (
              <tr key={pg.id} className="border-b border-neutral-50" data-testid={`page-${pg.id}`}>
                <td className="px-2 py-2 whitespace-nowrap">
                  <button onClick={() => move(pg.id, -1)} className="text-neutral-300 hover:text-neutral-900" data-testid={`page-up-${pg.id}`}><ChevronUp className="w-3.5 h-3.5" /></button>
                  <button onClick={() => move(pg.id, 1)} className="text-neutral-300 hover:text-neutral-900" data-testid={`page-down-${pg.id}`}><ChevronDown className="w-3.5 h-3.5" /></button>
                </td>
                <td className="px-3 py-2 font-medium">{pg.name}
                  {pg.seo_keyword && <p className="text-[11px] text-neutral-400 font-normal">SEO : {pg.seo_keyword}</p>}</td>
                {["status_content", "status_design", "status_dev", "status_seo", "status_validation"].map((field) => (
                  <td key={field} className="px-3 py-2">
                    <select className="text-xs border border-neutral-200 rounded px-1.5 py-1 bg-white" value={pg[field]}
                      onChange={(e) => setStatus(pg.id, field, e.target.value)} data-testid={`page-${field}-${pg.id}`}>
                      {WEB_PAGE_STATUS[field].map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                    </select>
                  </td>
                ))}
                <td className="px-3 py-2">
                  <button onClick={() => act(() => api.delete(`/web/projects/${p.id}/pages/${pg.id}`))}
                    className="text-neutral-300 hover:text-red-500" data-testid={`page-delete-${pg.id}`}><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
            {(p.pages || []).length === 0 && <tr><td colSpan={8} className="px-3 py-6 text-center text-sm text-neutral-400">Aucune page — définissez l'arborescence du site.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Tasks({ p, act }) {
  const [f, setF] = useState({ title: "", category: "developpement", estimate_min: "", due_date: "", priority: "normale" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const add = () => {
    if (!f.title.trim()) return;
    act(() => api.post(`/web/projects/${p.id}/tasks`, { ...f, estimate_min: Number(f.estimate_min) || 0, due_date: f.due_date || null }), "Tâche ajoutée");
    setF({ title: "", category: "developpement", estimate_min: "", due_date: "", priority: "normale" });
  };
  return (
    <div className="space-y-4" data-testid="web-tasks">
      <div className="bg-white border border-neutral-200 rounded-xl p-4 flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-48"><Label className="text-xs">Tâche *</Label>
          <Input className="mt-1" value={f.title} onChange={set("title")} placeholder="Ex : Développement de la page Services" data-testid="task-title" /></div>
        <div><Label className="text-xs">Catégorie</Label>
          <select className={`${sel} mt-1`} value={f.category} onChange={set("category")} data-testid="task-category">
            {WEB_TASK_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select></div>
        <div><Label className="text-xs">Durée estimée (min)</Label>
          <Input type="number" className="mt-1 w-24" value={f.estimate_min} onChange={set("estimate_min")} data-testid="task-estimate" /></div>
        <div><Label className="text-xs">Échéance</Label>
          <Input type="date" className="mt-1" value={f.due_date} onChange={set("due_date")} data-testid="task-due" /></div>
        <Button size="sm" className="bg-neutral-900 text-white" onClick={add} data-testid="task-add"><Plus className="w-4 h-4" /></Button>
      </div>
      {(p.tasks || []).map((t) => (
        <div key={t.id} className="bg-white border border-neutral-200 rounded-xl p-3.5 flex items-center gap-3" data-testid={`task-${t.id}`}>
          <select className="text-xs border border-neutral-200 rounded px-1.5 py-1 bg-white" value={t.status}
            onChange={(e) => act(() => api.patch(`/web/projects/${p.id}/tasks/${t.id}`, { status: e.target.value }))} data-testid={`task-status-${t.id}`}>
            <option value="a_faire">À faire</option><option value="en_cours">En cours</option>
            <option value="a_verifier">À vérifier</option><option value="termine">Terminé</option>
          </select>
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-medium ${t.status === "termine" ? "line-through text-neutral-400" : ""}`}>{t.title}</p>
            <p className="text-[11px] text-neutral-400">
              {WEB_TASK_CATEGORIES.find((c) => c.id === t.category)?.label}
              {t.estimate_min ? ` · estimé ${t.estimate_min} min` : ""} · réel {fmtH(t.spent_sec)}
              {t.due_date ? ` · échéance ${fmtD(t.due_date)}` : ""}
            </p>
          </div>
          <button onClick={() => act(() => api.delete(`/web/projects/${p.id}/tasks/${t.id}`))}
            className="text-neutral-300 hover:text-red-500" data-testid={`task-delete-${t.id}`}><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      {(p.tasks || []).length === 0 && <p className="text-sm text-neutral-400 text-center py-6">Aucune tâche pour l'instant.</p>}
    </div>
  );
}

function Messages({ p, act }) {
  const [text, setText] = useState("");
  const [internal, setInternal] = useState(false);
  const send = () => {
    if (!text.trim()) return;
    act(() => api.post(`/web/projects/${p.id}/messages`, { text: text.trim(), internal }), null);
    setText("");
  };
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-4" data-testid="web-messages">
      <h3 className="font-heading font-bold">Messages et historique</h3>
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {(p.messages || []).map((m) => (
          <div key={m.id} className={`rounded-lg px-3 py-2 text-sm ${m.internal ? "bg-amber-50 border border-amber-100" : m.by_client ? "bg-blue-50 border border-blue-100" : "bg-neutral-50 border border-neutral-100"}`} data-testid={`message-${m.id}`}>
            <p className="text-[11px] text-neutral-400 mb-0.5">
              {m.by} · {fmtD(m.at)}{m.internal && <span className="text-amber-600 font-semibold"> · Note interne (invisible client)</span>}
            </p>
            {m.text}
          </div>
        ))}
        {(p.messages || []).length === 0 && <p className="text-sm text-neutral-400">Aucun message.</p>}
      </div>
      <div className="flex items-end gap-2">
        <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Message au client ou note interne…" data-testid="message-input" />
        <div className="flex flex-col gap-2">
          <label className="flex items-center gap-1.5 text-xs text-neutral-500 cursor-pointer" data-testid="message-internal">
            <input type="checkbox" className="rounded" checked={internal} onChange={(e) => setInternal(e.target.checked)} /> Note interne
          </label>
          <Button size="sm" className="bg-neutral-900 text-white" onClick={send} data-testid="message-send"><Send className="w-4 h-4" /></Button>
        </div>
      </div>
      {(p.activity || []).length > 0 && (
        <div className="pt-3 border-t border-neutral-100">
          <p className="text-xs font-semibold text-neutral-400 mb-1">Historique</p>
          {[...(p.activity || [])].reverse().slice(0, 15).map((a, i) => (
            <p key={i} className="text-xs text-neutral-500 py-0.5">{fmtD(a.at)} — {a.text} <span className="text-neutral-300">({a.by})</span></p>
          ))}
        </div>
      )}
    </div>
  );
}

function Budget({ p, patch }) {
  const [invoices, setInvoices] = useState([]);
  useEffect(() => {
    api.get("/accounting/invoices").then((r) =>
      setInvoices((r.data || []).filter((i) => p.quote_id && i.quote_id === p.quote_id)));
  }, [p.id, p.quote_id]);
  const linked = invoices;
  const facture = linked.reduce((a, i) => a + (i.status !== "brouillon" ? i.total_ttc || 0 : 0), 0);
  const encaisse = linked.reduce((a, i) => a + (i.paid_total || 0), 0);
  const spentSec = (p.tasks || []).reduce((a, t) => a + (t.spent_sec || 0), 0);
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="web-budget">
      <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-2">
        <h3 className="font-heading font-bold">Montants</h3>
        <Info label="Montant contractuel" value={eur(p.amount)} />
        <Info label="Acompte prévu" value={eur(p.deposit)} />
        <Info label="Total facturé" value={eur(facture)} />
        <Info label="Total encaissé" value={eur(encaisse)} />
        <Info label="Solde restant" value={eur(Math.max(0, (p.amount || 0) - encaisse))} />
        {p.quote_id && (
          <a href={`/comptabilite?tab=devis&quote=${p.quote_id}`} className="text-xs text-blue-600 hover:underline" data-testid="budget-quote-link">
            Voir le devis associé dans la comptabilité
          </a>
        )}
      </div>
      <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-2">
        <h3 className="font-heading font-bold">Temps et rentabilité</h3>
        <Info label="Temps estimé" value={p.estimated_minutes ? `${Math.round(p.estimated_minutes / 60)} h` : "—"} />
        <Info label="Temps réalisé" value={fmtH(spentSec)} />
        <Info label="Temps restant estimé" value={p.estimated_minutes ? fmtH(Math.max(0, p.estimated_minutes * 60 - spentSec)) : "—"} />
        <h3 className="font-heading font-bold pt-3">Factures liées</h3>
        {linked.length === 0 && <p className="text-sm text-neutral-400">Aucune facture liée au devis du projet.</p>}
        {linked.map((i) => (
          <div key={i.id} className="flex justify-between text-sm" data-testid={`budget-invoice-${i.id}`}>
            <span>Facture {i.number || "brouillon"}</span>
            <span className="text-neutral-500">{eur(i.total_ttc)} · {i.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ================= Design, SEO, Tests, Mise en ligne, Maintenance =================

function Design({ p, act }) {
  const design = p.design || {};
  const versions = design.versions || [];
  const assets = design.assets || [];
  const [aForm, setAForm] = useState({ kind: "maquette_desktop", name: "", url: "" });
  const [aFile, setAFile] = useState(null);
  const [vForm, setVForm] = useState({ label: "", note: "", links: "" });
  const [vFiles, setVFiles] = useState([]);

  const addAsset = () => {
    const fd = new FormData();
    fd.append("kind", aForm.kind); fd.append("name", aForm.name); fd.append("url", aForm.url);
    if (aFile) fd.append("file", aFile);
    act(() => api.post(`/web/projects/${p.id}/design/assets`, fd), "Élément design ajouté");
    setAForm({ kind: "maquette_desktop", name: "", url: "" }); setAFile(null);
  };
  const addVersion = () => {
    const fd = new FormData();
    fd.append("label", vForm.label); fd.append("note", vForm.note); fd.append("links", vForm.links);
    vFiles.forEach((f) => fd.append("files", f));
    act(() => api.post(`/web/projects/${p.id}/design/versions`, fd), "Maquette créée (brouillon)");
    setVForm({ label: "", note: "", links: "" }); setVFiles([]);
  };
  return (
    <div className="space-y-4" data-testid="web-design">
      <div className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold">Maquettes et versions</h3>
          <span className="text-xs text-neutral-500" data-testid="design-roundtrips">
            Allers-retours : {design.roundtrips || 0}{p.roundtrips_planned ? ` / ${p.roundtrips_planned} prévus au devis` : ""}
          </span>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div><Label className="text-xs">Libellé</Label>
            <Input className="mt-1 w-28" value={vForm.label} onChange={(e) => setVForm({ ...vForm, label: e.target.value })} placeholder={`V${versions.length + 1}`} data-testid="design-version-label" /></div>
          <div className="flex-1 min-w-40"><Label className="text-xs">Liens (Figma…, un par ligne)</Label>
            <Input className="mt-1" value={vForm.links} onChange={(e) => setVForm({ ...vForm, links: e.target.value })} data-testid="design-version-links" /></div>
          <div><Label className="text-xs">Fichiers</Label>
            <Input type="file" multiple className="mt-1 text-xs w-48" onChange={(e) => setVFiles(Array.from(e.target.files || []))} data-testid="design-version-files" /></div>
          <Button size="sm" className="bg-neutral-900 text-white" onClick={addVersion} data-testid="design-version-add"><Plus className="w-4 h-4" /></Button>
        </div>
        {versions.map((v) => {
          const st = WEB_VERSION_STATUS[v.status] || WEB_VERSION_STATUS.brouillon;
          return (
            <div key={v.id} className="border border-neutral-100 rounded-lg p-3 flex items-center justify-between gap-3" data-testid={`design-version-${v.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-semibold">{v.label} <span className="text-[11px] text-neutral-400 font-normal">{fmtD(v.at)} · {v.files?.length || 0} fichier(s), {v.links?.length || 0} lien(s)</span></p>
                {v.note && <p className="text-xs text-neutral-500">{v.note}</p>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge st={st} />
                {["brouillon", "modifications"].includes(v.status) && (
                  <Button size="sm" variant="outline" className="h-7 text-xs"
                    onClick={() => act(() => api.post(`/web/projects/${p.id}/design/versions/${v.id}/send`), "Maquette envoyée au client pour validation")}
                    data-testid={`design-send-${v.id}`}>Envoyer pour validation</Button>
                )}
              </div>
            </div>
          );
        })}
        {versions.length === 0 && <p className="text-sm text-neutral-400">Aucune maquette pour l'instant.</p>}
      </div>

      <div className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3">
        <h3 className="font-heading font-bold">Moodboard, inspirations, palette…</h3>
        <div className="flex flex-wrap items-end gap-2">
          <div><Label className="text-xs">Type</Label>
            <select className={`${sel} mt-1`} value={aForm.kind} onChange={(e) => setAForm({ ...aForm, kind: e.target.value })} data-testid="design-asset-kind">
              {WEB_DESIGN_KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
            </select></div>
          <div className="flex-1 min-w-40"><Label className="text-xs">Nom</Label>
            <Input className="mt-1" value={aForm.name} onChange={(e) => setAForm({ ...aForm, name: e.target.value })} data-testid="design-asset-name" /></div>
          <div><Label className="text-xs">Lien</Label>
            <Input className="mt-1 w-44" value={aForm.url} onChange={(e) => setAForm({ ...aForm, url: e.target.value })} placeholder="https://…" data-testid="design-asset-url" /></div>
          <div><Label className="text-xs">Fichier</Label>
            <Input type="file" className="mt-1 text-xs w-44" onChange={(e) => setAFile(e.target.files[0] || null)} data-testid="design-asset-file" /></div>
          <Button size="sm" variant="outline" onClick={addAsset} data-testid="design-asset-add"><Plus className="w-4 h-4" /></Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {assets.map((a) => (
            <span key={a.id} className="inline-flex items-center gap-1.5 text-xs border border-neutral-200 rounded-full px-2.5 py-1" data-testid={`design-asset-${a.id}`}>
              {WEB_DESIGN_KINDS.find((k) => k.id === a.kind)?.label} · {a.name}
              {a.url && <a href={a.url} target="_blank" rel="noreferrer" className="text-blue-600">↗</a>}
              <button onClick={() => act(() => api.delete(`/web/projects/${p.id}/design/assets/${a.id}`))} className="text-neutral-300 hover:text-red-500" data-testid={`design-asset-del-${a.id}`}><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function Seo({ p, act }) {
  const seo = p.seo_global || {};
  const toggle = (k) => act(() => api.patch(`/web/projects/${p.id}/seo-global`, { [k]: !seo[k] }));
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="web-seo">
      <div className="bg-white border border-neutral-200 rounded-xl p-5">
        <h3 className="font-heading font-bold mb-3">SEO global</h3>
        {WEB_SEO_GLOBAL.map(([k, label]) => (
          <label key={k} className="flex items-center gap-2 text-sm py-1 cursor-pointer" data-testid={`seo-global-${k}`}>
            <input type="checkbox" className="rounded" checked={!!seo[k]} onChange={() => toggle(k)} />
            {label}
          </label>
        ))}
      </div>
      <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-3">
        <h3 className="font-heading font-bold">SEO page par page</h3>
        <p className="text-xs text-neutral-400">Une page techniquement terminée n'est pas automatiquement optimisée SEO — validez chaque page.</p>
        {(p.pages || []).map((pg) => (
          <div key={pg.id} className="border border-neutral-100 rounded-lg p-3 space-y-2" data-testid={`seo-page-${pg.id}`}>
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold">{pg.name}</p>
              <select className="text-xs border border-neutral-200 rounded px-1.5 py-1 bg-white" value={pg.status_seo}
                onChange={(e) => act(() => api.patch(`/web/projects/${p.id}/pages/${pg.id}`, { status_seo: e.target.value }))}
                data-testid={`seo-status-${pg.id}`}>
                <option value="a_faire">Non commencé</option><option value="en_cours">En cours</option>
                <option value="a_verifier">À vérifier</option><option value="valide">Validé</option>
              </select>
            </div>
            <Input className="!h-8 text-xs" defaultValue={pg.title_seo || ""} placeholder="Title SEO"
              onBlur={(e) => e.target.value !== (pg.title_seo || "") && act(() => api.patch(`/web/projects/${p.id}/pages/${pg.id}`, { title_seo: e.target.value }))}
              data-testid={`seo-title-${pg.id}`} />
            <Input className="!h-8 text-xs" defaultValue={pg.meta_desc || ""} placeholder="Meta description"
              onBlur={(e) => e.target.value !== (pg.meta_desc || "") && act(() => api.patch(`/web/projects/${p.id}/pages/${pg.id}`, { meta_desc: e.target.value }))}
              data-testid={`seo-meta-${pg.id}`} />
          </div>
        ))}
        {(p.pages || []).length === 0 && <p className="text-sm text-neutral-400">Définissez d'abord l'arborescence.</p>}
      </div>
    </div>
  );
}

function Tests({ p, act }) {
  const [f, setF] = useState({ title: "", category: "fonctionnalites", priority: "normale", page_id: "", description: "" });
  const [shot, setShot] = useState(null);
  const [deroFor, setDeroFor] = useState(null);
  const [deroReason, setDeroReason] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const add = () => {
    if (!f.title.trim()) { toast.error("Décrivez l'anomalie"); return; }
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    if (shot) fd.append("screenshot", shot);
    act(() => api.post(`/web/projects/${p.id}/anomalies`, fd), "Anomalie créée");
    setF({ title: "", category: "fonctionnalites", priority: "normale", page_id: "", description: "" }); setShot(null);
  };
  const anomalies = p.anomalies || [];
  const blocking = anomalies.filter((a) => a.priority === "bloquante" && a.status !== "resolu" && !a.derogation);
  return (
    <div className="space-y-4" data-testid="web-tests">
      {blocking.length > 0 && (
        <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3" data-testid="tests-blocking">
          {blocking.length} anomalie(s) bloquante(s) non résolue(s) — la mise en ligne est bloquée sauf dérogation.
        </p>
      )}
      <div className="bg-white border border-neutral-200 rounded-xl p-4 flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-48"><Label className="text-xs">Problème *</Label>
          <Input className="mt-1" value={f.title} onChange={set("title")} placeholder="Ex : le formulaire ne s'envoie pas" data-testid="anomaly-title" /></div>
        <div><Label className="text-xs">Catégorie</Label>
          <select className={`${sel} mt-1`} value={f.category} onChange={set("category")} data-testid="anomaly-category">
            {WEB_ANOMALY_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select></div>
        <div><Label className="text-xs">Priorité</Label>
          <select className={`${sel} mt-1`} value={f.priority} onChange={set("priority")} data-testid="anomaly-priority">
            {["basse", "normale", "haute", "bloquante"].map((x) => <option key={x} value={x}>{x}</option>)}
          </select></div>
        <div><Label className="text-xs">Page</Label>
          <select className={`${sel} mt-1`} value={f.page_id} onChange={set("page_id")} data-testid="anomaly-page">
            <option value="">—</option>
            {(p.pages || []).map((pg) => <option key={pg.id} value={pg.id}>{pg.name}</option>)}
          </select></div>
        <div><Label className="text-xs">Capture</Label>
          <Input type="file" accept="image/*" className="mt-1 text-xs w-40" onChange={(e) => setShot(e.target.files[0] || null)} data-testid="anomaly-screenshot" /></div>
        <Button size="sm" className="bg-neutral-900 text-white" onClick={add} data-testid="anomaly-add"><Plus className="w-4 h-4" /></Button>
      </div>
      {anomalies.map((a) => {
        const st = WEB_ANOMALY_STATUS[a.status] || WEB_ANOMALY_STATUS.a_corriger;
        return (
          <div key={a.id} className="bg-white border border-neutral-200 rounded-xl p-3.5 flex items-center gap-3" data-testid={`anomaly-${a.id}`}>
            <select className="text-xs border border-neutral-200 rounded px-1.5 py-1 bg-white" value={a.status}
              onChange={(e) => act(() => api.patch(`/web/projects/${p.id}/anomalies/${a.id}`, { status: e.target.value }))}
              data-testid={`anomaly-status-${a.id}`}>
              {Object.entries(WEB_ANOMALY_STATUS).map(([id, s]) => <option key={id} value={id}>{s.label}</option>)}
            </select>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${a.status === "resolu" ? "line-through text-neutral-400" : ""}`}>{a.title}</p>
              <p className="text-[11px] text-neutral-400">
                {WEB_ANOMALY_CATEGORIES.find((c) => c.id === a.category)?.label} · {a.priority}
                {a.priority === "bloquante" && a.status !== "resolu" && <span className="text-red-600 font-semibold"> · BLOQUANTE</span>}
                {a.derogation && <span className="text-amber-600"> · dérogation : {a.derogation.reason}</span>}
              </p>
            </div>
            {a.priority === "bloquante" && a.status !== "resolu" && !a.derogation && (
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setDeroFor(a.id); setDeroReason(""); }} data-testid={`anomaly-derogate-${a.id}`}>Dérogation</Button>
            )}
          </div>
        );
      })}
      {deroFor && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-2">
          <Input value={deroReason} onChange={(e) => setDeroReason(e.target.value)} placeholder="Motif de la dérogation (obligatoire)…" data-testid="anomaly-derogation-reason" />
          <Button size="sm" variant="outline" disabled={!deroReason.trim()}
            onClick={() => { act(() => api.post(`/web/projects/${p.id}/anomalies/${deroFor}/derogation`, { reason: deroReason }), "Dérogation enregistrée"); setDeroFor(null); }}
            data-testid="anomaly-derogation-confirm">Enregistrer</Button>
          <Button size="sm" variant="ghost" onClick={() => setDeroFor(null)}>Annuler</Button>
        </div>
      )}
      {anomalies.length === 0 && <p className="text-sm text-neutral-400 text-center py-6">Aucune anomalie signalée.</p>}
    </div>
  );
}

function GoLive({ p, act }) {
  const go = p.golive || {};
  const checklist = go.checklist || {};
  const [fields, setFields] = useState({ domain: go.domain || p.domain || "", production_url: go.production_url || "", planned_date: go.planned_date || "" });
  const missing = WEB_GOLIVE_CHECKLIST.filter(([k]) => !checklist[k]);
  const toggle = (k) => act(() => api.patch(`/web/projects/${p.id}/golive`, { checklist: { [k]: !checklist[k] } }));
  const saveFields = () => act(() => api.patch(`/web/projects/${p.id}/golive`, fields), "Informations enregistrées");
  const confirm = () => act(() => api.post(`/web/projects/${p.id}/golive/confirm`), "Site mis en ligne 🚀");
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="web-golive">
      <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-3">
        <h3 className="font-heading font-bold">Informations de livraison</h3>
        <div><Label className="text-xs">Domaine définitif</Label>
          <Input className="mt-1" value={fields.domain} onChange={(e) => setFields({ ...fields, domain: e.target.value })} data-testid="golive-domain" /></div>
        <div><Label className="text-xs">URL de production</Label>
          <Input className="mt-1" value={fields.production_url} onChange={(e) => setFields({ ...fields, production_url: e.target.value })} placeholder="https://…" data-testid="golive-url" /></div>
        <div><Label className="text-xs">Date de mise en ligne prévue</Label>
          <Input type="date" className="mt-1" value={fields.planned_date} onChange={(e) => setFields({ ...fields, planned_date: e.target.value })} data-testid="golive-date" /></div>
        <Button size="sm" variant="outline" onClick={saveFields} data-testid="golive-save">Enregistrer</Button>
        {go.confirmed_at && (
          <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2" data-testid="golive-confirmed">
            ✓ Site en ligne depuis le {fmtD(go.confirmed_at)} (par {go.by})
          </p>
        )}
      </div>
      <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-3">
        <h3 className="font-heading font-bold">Checklist de mise en ligne ({WEB_GOLIVE_CHECKLIST.length - missing.length}/{WEB_GOLIVE_CHECKLIST.length})</h3>
        <div className="grid grid-cols-1 gap-0.5 max-h-64 overflow-y-auto">
          {WEB_GOLIVE_CHECKLIST.map(([k, label]) => (
            <label key={k} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer" data-testid={`golive-check-${k}`}>
              <input type="checkbox" className="rounded" checked={!!checklist[k]} onChange={() => toggle(k)} disabled={!!go.confirmed_at} />
              {label}
            </label>
          ))}
        </div>
        {!go.confirmed_at && (
          <Button onClick={confirm} disabled={missing.length > 0} className="w-full bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="golive-confirm">
            Confirmer la mise en ligne
          </Button>
        )}
        {missing.length > 0 && !go.confirmed_at && <p className="text-[11px] text-neutral-400">Complétez la checklist pour activer le bouton (validation client de préproduction requise, aucune anomalie bloquante).</p>}
        {["en_ligne", "recette"].includes(p.stage) && (
          <Button variant="outline" className="w-full" onClick={() => act(() => api.post(`/web/projects/${p.id}/cloture`), "Projet clôturé")} data-testid="golive-cloture">
            Clôturer le projet (recette terminée)
          </Button>
        )}
      </div>
    </div>
  );
}

function Maintenance({ p, act }) {
  const mnt = p.maintenance || {};
  const [f, setF] = useState({ tarif: mnt.tarif || "", monthly_minutes: mnt.monthly_minutes || "", update_freq: mnt.update_freq || "", backup_freq: mnt.backup_freq || "", prestations: mnt.prestations || "" });
  const [entry, setEntry] = useState({ kind: "maj", label: "", spent_min: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = () => act(() => api.patch(`/web/projects/${p.id}/maintenance`, { ...f, active: true }), "Contrat de maintenance enregistré");
  const addEntry = () => {
    if (!entry.label.trim()) return;
    act(() => api.post(`/web/projects/${p.id}/maintenance/entries`, { ...entry, spent_min: Number(entry.spent_min) || 0 }), "Entrée ajoutée");
    setEntry({ kind: "maj", label: "", spent_min: "" });
  };
  const reports = p.reports || [];
  const entries = mnt.entries || [];
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="web-maintenance">
      <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold">Contrat de maintenance</h3>
          {mnt.active
            ? <span className="text-[11px] text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">Actif</span>
            : <span className="text-[11px] text-neutral-400 border border-neutral-200 rounded-full px-2 py-0.5">Inactif</span>}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs">Tarif (€/mois)</Label>
            <Input type="number" className="mt-1" value={f.tarif} onChange={set("tarif")} data-testid="mnt-tarif" /></div>
          <div><Label className="text-xs">Temps mensuel inclus (min)</Label>
            <Input type="number" className="mt-1" value={f.monthly_minutes} onChange={set("monthly_minutes")} data-testid="mnt-minutes" /></div>
          <div><Label className="text-xs">Fréquence mises à jour</Label>
            <Input className="mt-1" value={f.update_freq} onChange={set("update_freq")} placeholder="mensuelle" data-testid="mnt-update-freq" /></div>
          <div><Label className="text-xs">Fréquence sauvegardes</Label>
            <Input className="mt-1" value={f.backup_freq} onChange={set("backup_freq")} placeholder="hebdomadaire" data-testid="mnt-backup-freq" /></div>
        </div>
        <div><Label className="text-xs">Prestations incluses</Label>
          <Textarea rows={2} className="mt-1" value={f.prestations} onChange={set("prestations")} data-testid="mnt-prestations" /></div>
        <Button size="sm" onClick={save} className="bg-neutral-900 text-white" data-testid="mnt-save">
          {mnt.active ? "Mettre à jour le contrat" : "Activer le contrat de maintenance"}
        </Button>
        {reports.length > 0 && (
          <div className="pt-3 border-t border-neutral-100 space-y-2">
            <p className="text-xs font-semibold text-neutral-500">Signalements client</p>
            {reports.map((r) => (
              <div key={r.id} className="border border-neutral-100 rounded-lg p-2.5 text-sm" data-testid={`mnt-report-${r.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium">{r.subject} <span className="text-[11px] text-neutral-400">({r.urgency})</span></p>
                  <div className="flex items-center gap-2">
                    <select className="text-xs border border-neutral-200 rounded px-1.5 py-1 bg-white" value={r.status}
                      onChange={(e) => act(() => api.patch(`/web/projects/${p.id}/reports/${r.id}`, { status: e.target.value }))} data-testid={`mnt-report-status-${r.id}`}>
                      <option value="nouveau">Nouveau</option><option value="en_cours">En cours</option>
                      <option value="resolu">Résolu</option><option value="devis_a_faire">Devis à faire</option>
                    </select>
                    <select className="text-xs border border-neutral-200 rounded px-1.5 py-1 bg-white"
                      value={r.in_contract === null || r.in_contract === undefined ? "" : String(r.in_contract)}
                      onChange={(e) => act(() => api.patch(`/web/projects/${p.id}/reports/${r.id}`, { in_contract: e.target.value === "true" }))} data-testid={`mnt-report-contract-${r.id}`}>
                      <option value="">Contrat ?</option><option value="true">Inclus au contrat</option><option value="false">Devis complémentaire</option>
                    </select>
                  </div>
                </div>
                {r.description && <p className="text-xs text-neutral-500 mt-1">{r.description}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-3">
        <h3 className="font-heading font-bold">Suivi des interventions</h3>
        <div className="flex flex-wrap items-end gap-2">
          <div><Label className="text-xs">Type</Label>
            <select className={`${sel} mt-1`} value={entry.kind} onChange={(e) => setEntry({ ...entry, kind: e.target.value })} data-testid="mnt-entry-kind">
              <option value="maj">Mise à jour</option><option value="sauvegarde">Sauvegarde</option>
              <option value="incident">Incident</option><option value="correction">Correction</option>
              <option value="demande">Demande supplémentaire</option>
            </select></div>
          <div className="flex-1 min-w-40"><Label className="text-xs">Libellé</Label>
            <Input className="mt-1" value={entry.label} onChange={(e) => setEntry({ ...entry, label: e.target.value })} data-testid="mnt-entry-label" /></div>
          <div><Label className="text-xs">Temps (min)</Label>
            <Input type="number" className="mt-1 w-20" value={entry.spent_min} onChange={(e) => setEntry({ ...entry, spent_min: e.target.value })} data-testid="mnt-entry-min" /></div>
          <Button size="sm" variant="outline" onClick={addEntry} data-testid="mnt-entry-add"><Plus className="w-4 h-4" /></Button>
        </div>
        {entries.map((e) => (
          <div key={e.id} className="flex justify-between text-sm border-b border-neutral-50 py-1.5" data-testid={`mnt-entry-${e.id}`}>
            <span>{e.label} <span className="text-[11px] text-neutral-400">({e.kind}{e.in_contract === false ? ", hors contrat" : ""})</span></span>
            <span className="text-neutral-500">{e.spent_min ? `${e.spent_min} min · ` : ""}{fmtD(e.at)}</span>
          </div>
        ))}
        {entries.length === 0 && <p className="text-sm text-neutral-400">Aucune intervention enregistrée.</p>}
      </div>
    </div>
  );
}
